/**
 * Strategy A: synchronous first paint for fixed_time countdown timers.
 * Exposes GcwCountdownMath for countdown-timer.js (shared adaptive math).
 */
(function (global) {
  var MS_IN_SEC = 1000;
  var MS_IN_MIN = 60 * MS_IN_SEC;
  var MS_IN_HRS = 60 * MS_IN_MIN;
  var MS_IN_DAY = 24 * MS_IN_HRS;

  function parseHoursThreshold(value) {
    var parsed = parseInt(value, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 36;
  }

  /** Theme editor format: YYYY/MM/DD hh:mm AM/PM — explicit parse for Safari/consistency. */
  function parseDueDate(value) {
    if (!value) {
      return NaN;
    }

    var trimmed = String(value).trim();
    var match = trimmed.match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})\s+(\d{1,2}):(\d{2})\s*(AM|PM)$/i);

    if (match) {
      var year = parseInt(match[1], 10);
      var month = parseInt(match[2], 10) - 1;
      var day = parseInt(match[3], 10);
      var hour = parseInt(match[4], 10);
      var minute = parseInt(match[5], 10);
      var ampm = match[6].toUpperCase();

      if (ampm === 'PM' && hour < 12) {
        hour += 12;
      }
      if (ampm === 'AM' && hour === 12) {
        hour = 0;
      }

      return new Date(year, month, day, hour, minute, 0, 0).getTime();
    }

    var native = Date.parse(trimmed);
    return isNaN(native) ? NaN : native;
  }

  function syncTimerBoxMode(el, mode) {
    var box = el.closest('.op-e__timer-box--adaptive');
    if (!box) {
      return;
    }

    var isDaysMode = mode === 'days';
    box.classList.toggle('is-days-mode', isDaysMode);
    box.classList.toggle('is-hours-mode', !isDaysMode);
  }

  function hasDaySlot(el) {
    return !!el.querySelector('[data-timer-day]');
  }

  function resolveDisplayMode(el, remainingMs) {
    var hasDay = hasDaySlot(el);
    var isAdaptive = el.dataset.displayMode === 'adaptive';

    if (hasDay && !isAdaptive) {
      return 'days';
    }

    if (isAdaptive && hasDay) {
      var threshold = parseHoursThreshold(el.dataset.hoursThreshold);
      return remainingMs > threshold * MS_IN_HRS ? 'days' : 'hours';
    }

    return 'hours';
  }

  function computeTimeComponents(ms, mode) {
    if (mode === 'days') {
      return {
        day: Math.floor(ms / MS_IN_DAY),
        hour: Math.floor((ms % MS_IN_DAY) / MS_IN_HRS),
        minute: Math.floor((ms % MS_IN_HRS) / MS_IN_MIN),
        second: Math.floor((ms % MS_IN_MIN) / MS_IN_SEC),
      };
    }

    return {
      hour: Math.floor(ms / MS_IN_HRS),
      minute: Math.floor((ms % MS_IN_HRS) / MS_IN_MIN),
      second: Math.floor((ms % MS_IN_MIN) / MS_IN_SEC),
    };
  }

  function formatTimeComponent(value) {
    return value < 10 ? '0' + value : String(value);
  }

  function applyDisplayMode(el, mode) {
    var dayEl = el.querySelector('[data-timer-day]');
    if (!dayEl) {
      return;
    }

    var isAdaptive = el.dataset.displayMode === 'adaptive';
    var daySep = el.querySelector('[data-timer-day-sep]');
    var isDaysMode = mode === 'days';

    if (isAdaptive) {
      el.classList.toggle('is-days-mode', isDaysMode);
      el.classList.toggle('is-hours-mode', !isDaysMode);
      syncTimerBoxMode(el, mode);
      var daySegment = el.querySelector('[data-timer-day-segment]');
      if (daySegment) {
        daySegment.hidden = !isDaysMode;
      } else {
        if (daySep) {
          daySep.hidden = !isDaysMode;
        }
        dayEl.hidden = !isDaysMode;
      }
      return;
    }

    el.classList.add('is-days-mode');
    el.classList.remove('is-hours-mode');
    syncTimerBoxMode(el, 'days');
  }

  function paintTimerElement(el) {
    if (el.dataset.type !== 'fixed_time') {
      return false;
    }

    var due = el.dataset.dueDate;
    if (!due) {
      return false;
    }

    var targetTime = parseDueDate(due);
    if (!targetTime || isNaN(targetTime)) {
      return false;
    }

    var remaining = Math.max(0, targetTime - Date.now());
    if (remaining <= 0) {
      return false;
    }

    var mode = resolveDisplayMode(el, remaining);
    var parts = computeTimeComponents(remaining, mode);
    var visual = el.querySelector('[data-timer-visual]') || el;

    applyDisplayMode(el, mode);

    var dayEl = visual.querySelector('[data-timer-day]') || el.querySelector('[data-timer-day]');
    var hourEl = visual.querySelector('[data-timer-hour]') || el.querySelector('[data-timer-hour]');
    var minEl = visual.querySelector('[data-timer-minute]') || el.querySelector('[data-timer-minute]');
    var secEl = visual.querySelector('[data-timer-second]') || el.querySelector('[data-timer-second]');

    if (dayEl && parts.day !== undefined) {
      dayEl.textContent = formatTimeComponent(parts.day);
      if (el.dataset.displayMode === 'adaptive') {
        dayEl.hidden = mode !== 'days';
      }
    }

    var dayLabelEl = el.querySelector('[data-timer-day-label]');
    if (dayLabelEl && parts.day !== undefined) {
      var dayLabelText = parts.day === 1 ? dayLabelEl.dataset.singular : dayLabelEl.dataset.plural;
      if (dayLabelText) {
        dayLabelEl.textContent = dayLabelText;
      }
    }

    if (hourEl && parts.hour !== undefined) {
      hourEl.textContent = formatTimeComponent(parts.hour);
    }
    if (minEl && parts.minute !== undefined) {
      minEl.textContent = formatTimeComponent(parts.minute);
    }
    if (secEl && parts.second !== undefined) {
      secEl.textContent = formatTimeComponent(parts.second);
    }

    el.dataset.paintSource = 'sync';
    el.classList.remove('hidden');
    return true;
  }

  function paintAll() {
    if (typeof Shopify !== 'undefined' && Shopify.designMode) {
      return;
    }

    var timers = document.querySelectorAll('countdown-timer[data-due-date]');
    for (var i = 0; i < timers.length; i++) {
      paintTimerElement(timers[i]);
    }
  }

  global.GcwCountdownMath = {
    MS_IN_SEC: MS_IN_SEC,
    MS_IN_MIN: MS_IN_MIN,
    MS_IN_HRS: MS_IN_HRS,
    MS_IN_DAY: MS_IN_DAY,
    parseHoursThreshold: parseHoursThreshold,
    parseDueDate: parseDueDate,
    syncTimerBoxMode: syncTimerBoxMode,
    resolveDisplayMode: resolveDisplayMode,
    computeTimeComponents: computeTimeComponents,
    formatTimeComponent: formatTimeComponent,
    applyDisplayMode: applyDisplayMode,
    paintTimerElement: paintTimerElement,
    paintAll: paintAll,
  };

  paintAll();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', paintAll);
  }
})(window);
